import {
	Button,
	CircularProgress,
	Grid2,
	Paper,
	TextField,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { get } from "lodash-es";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import { useUTLSettingsContext } from "../../../../../context/UTLSettingContext";
import { ShiftData, useShiftNavigator } from "../../../../../controls";
import { AssetsFilter } from "../../../../../controls/asset-filter/AssetFilter";
import { AssetTreePicker } from "../../../../../controls/asset-tree-picker/AssetTreePicker";
import { ConfirmationDialogRaw } from "../../../../../controls/modals/ConfirmationDialogRaw";
import { ShiftAutocomplete } from "../../../../../controls/shift-selector/ShiftAutocomplete";
import { ShiftSelector } from "../../../../../controls/shift-selector/ShiftSelector";
import { NotificationSnackBar } from "../../../../../controls/snackbars/notification-snackbar";
import { HelmetDexteel } from "../../../../../helmet/HelmetDexteel";
import type { LogbookEntryActionsContext } from "../../../../../reducers/LogbookSettingsReducer";
import { TimeService, getMomentTz } from "../../../../../services";
import { useEntrySubmission } from "../../hooks/useEntrySubmission";
import { ENTRY_INITIAL_VALUES, Entry } from "../../models/Entry";
import { ContentInput } from "./ContentInput";

const moment = getMomentTz();

type Props = {
	entry: Entry | null;
	show: "edit" | "view" | "";
	onChangeShow: () => void;
	onHide: (entry?: number | null, shouldStayInEditMode?: boolean) => void;
	preselectedAssetId?: number | null;
	withAssetFilter?: "tree" | "selector";
	canEditAsset?: boolean;
	canEditShift?: boolean;
	filterAssets?: "area";
	allowAttachments?: boolean;
	shiftsRange?: number;
	entryActions?: (context: LogbookEntryActionsContext) => React.ReactNode;
};

export const EntryViewer = ({
	entry,
	show,
	onChangeShow,
	onHide,
	preselectedAssetId,
	withAssetFilter,
	canEditAsset,
	canEditShift,
	filterAssets,
	allowAttachments,
	shiftsRange,
	entryActions,
}: Props) => {
	const {
		state: { settings },
	} = useUTLSettingsContext();

	const defaultAssetId = Number(get(settings, "DefaultAssetId", 15));

	const { shiftId: shiftIdParam } = useParams();
	const [searchParams] = useSearchParams();
	const entryId = searchParams.get("entryId");
	const action = searchParams.get("action");

	const { shiftInfo } = useShiftNavigator();

	const isNewEntry = action === "new";

	const [notificationMessage, setNotificationMessage] = useState("");
	const [shiftTimestamp, setShiftTimestamp] = useState<Date | null>(null);
	const [error, setError] = useState("");

	const {
		control,
		handleSubmit,
		reset,
		formState: { isValid, dirtyFields },
		watch,
		setValue,
	} = useForm<Entry>();

	const isFormDirty = !(Object.keys(dirtyFields).length === 0);

	const updatedBy = watch("UpdatedBy");
	const updateTimestamp = watch("UpdateTimestamp");
	const createTimestamp = watch("CreateTimestamp");
	const content = watch("Content");
	const entryIdValue = watch("EntryId");
	const title = watch("Title");
	const author = watch("Author");
	const shiftId = watch("ShiftId");
	const assetId = watch("AssetId");
	const shift = watch("Shift");
	const crew = watch("Crew");

	// Handler for when entry is submitted via attachment modal
	const handleEntrySubmitted = (newEntryId: number, newEntryData?: any) => {
		setNotificationMessage("Entry created successfully");

		// If we have the new entry data, update the form immediately
		if (newEntryData) {
			reset(newEntryData);
		}

		// Add a setTimeout to delay navigation, giving the system time to process everything
		setTimeout(() => {
			onHide(newEntryId, true); // true = shouldStayInEditMode
		}, 1000); // 1000ms delay to ensure all processing is complete
	};

	// Use the shared entry submission hook
	const {
		submitEntry,
		isSubmitLoading,
		showConfirmationDialog,
		handleConfirmationClose,
	} = useEntrySubmission({
		onSuccess: (entry) => {
			setNotificationMessage(
				entryId === "new"
					? "Entry created successfully"
					: "Entry updated successfully",
			);
			// Ensure we have a valid EntryId before navigating
			if (entry && entry.EntryId) {
				// Add a setTimeout to delay navigation, giving the system time to process everything
				setTimeout(() => {
					onHide(entry.EntryId);
				}, 1000); // 1000ms delay to ensure all processing is complete
			} else {
				console.error("No EntryId received from API response", entry);
				setError("Failed to get entry ID from server response");
			}
		},
		onError: (error) => {
			setError(error.message);
		},
		isNewEntry,
		onHide: () => onHide(),
	});

	const onSubmit = async (formData: Entry) => {
		setError("");
		const submissionData = {
			assetId: formData.AssetId,
			entryId: formData.EntryId,
			shiftId: formData.ShiftId,
			isForOtherShift: entry?.ShiftId
				? entry.ShiftId !== formData.ShiftId
				: shiftInfo?.CurrentShiftId !== formData.ShiftId,
			title: formData.Title,
			content: formData.Content,
			author: formData.Author ?? "",
			createTimestamp: isNewEntry ? new Date() : formData.CreateTimestamp!,
		};

		await submitEntry(submissionData);
	};

	useEffect(() => {
		if (shiftInfo || !isNewEntry) {
			const newEntry = {
				...ENTRY_INITIAL_VALUES,
				ShiftId: shiftInfo?.CurrentShiftId!,
				Shift: shiftInfo?.CurrentShift ?? "",
				Crew: shiftInfo?.CurrentCrew ?? "",
				CreateTimestamp: new Date(),
				AssetId: preselectedAssetId ?? defaultAssetId ?? null,
			};
			reset((isNewEntry ? newEntry : entry) || ENTRY_INITIAL_VALUES);
		}
	}, [entry, shiftInfo, entryId, reset, defaultAssetId]);

	useEffect(() => {
		if (isNewEntry) {
			setValue("AssetId", preselectedAssetId ?? defaultAssetId ?? null);
		}
	}, [preselectedAssetId, defaultAssetId]);

	return (
		<>
			{isNewEntry || entry?.EntryId ? (
				<HelmetDexteel
					title={`${
						isNewEntry ? "New Entry" : `'${entry?.Title}' by ${entry?.Author}`
					} - Logbook Entry`}
				/>
			) : null}
			{isNewEntry || entry?.EntryId ? (
				<Paper
					elevation={1}
					sx={{
						width: "100%",
						height: "100%",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Grid2
						container
						spacing={1}
						component={"form"}
						onSubmit={handleSubmit(onSubmit)}
						height={"100%"}
						flexDirection={"column"}
						flexWrap={"nowrap"}
						p={1}
					>
						<Grid2 size={{ xs: 12 }}>
							<Controller
								name="Title"
								control={control}
								rules={{
									required: "Title is required",
									validate: (value) =>
										!!value?.trim() || "Title cannot be empty",
								}}
								render={({
									field: { value, onChange },
									fieldState: { error },
								}) => (
									<TextField
										label="Title"
										value={value ?? ""}
										onChange={onChange}
										error={!!error}
										helperText={error?.message}
										autoFocus={isNewEntry}
										size="medium"
									/>
								)}
							/>
						</Grid2>
						<Grid2 container size={{ xs: 12 }} spacing={2}>
							<Grid2 size={{ xs: 12, md: 3 }}>
								<Controller
									name="CreateTimestamp"
									control={control}
									render={({ field: { value } }) => (
										<DateTimePicker
											label="Creation Time"
											timezone={TimeService.getInstance().getServerTimeZone()}
											format="MM/DD/YYYY HH:mm"
											value={value ? moment(value) : null}
											disabled
											slotProps={{
												textField: {
													variant: "outlined",
													size: "small",
													fullWidth: true,
													InputLabelProps: {
														shrink: true,
														style: {
															fontSize: ".875rem",
															marginTop: -2,
														},
													},
												},
											}}
										/>
									)}
								/>
							</Grid2>
							{canEditShift ? (
								<Grid2 size={{ xs: 12, md: 3 }}>
									<Controller
										name="ShiftId"
										control={control}
										render={({ field }) => (
											<ShiftAutocomplete
												currentShiftId={
													Number(shiftIdParam) ?? shiftInfo?.CurrentShiftId
												}
												onShiftSelected={(selectedShift: ShiftData | null) => {
													if (selectedShift) {
														setValue("Shift", selectedShift.Shift, {
															shouldDirty: true,
														});
														setValue("Crew", selectedShift.Crew, {
															shouldDirty: true,
														});
														setShiftTimestamp(new Date(selectedShift.Start));
														field.onChange(selectedShift.ShiftId);
													} else {
														setValue("Shift", null, { shouldDirty: true });
														setValue("Crew", null, { shouldDirty: true });
														setShiftTimestamp(null);
														field.onChange(null);
													}
												}}
												value={field.value as number | null}
												disabled={!isNewEntry && show !== "edit"}
												shiftsRange={shiftsRange}
											/>
										)}
									/>
								</Grid2>
							) : (
								<Grid2 size={{ xs: 12, md: 3 }}>
									<Controller
										name="ShiftId"
										control={control}
										render={({ field }) => (
											<ShiftSelector
												onChange={field.onChange}
												value={field.value as number | null}
												disabled
											/>
										)}
									/>
								</Grid2>
							)}
							<Grid2 size={{ xs: 12, md: canEditAsset ? 2 : 6 }}>
								<Controller
									name="Author"
									control={control}
									rules={{
										required: "Author is required",
									}}
									render={({
										field: { value, onChange },
										fieldState: { error },
									}) => (
										<TextField
											label="Author"
											variant="outlined"
											value={value ?? ""}
											onChange={onChange}
											fullWidth
											error={!!error}
											helperText={error?.message}
											disabled={!isNewEntry}
											size="small"
										/>
									)}
								/>
							</Grid2>
							{canEditAsset ? (
								withAssetFilter === "tree" ? (
									<Grid2 size={{ md: 4, xs: 12 }}>
										<Controller
											name="AssetId"
											control={control}
											render={({ field }) => (
												<AssetTreePicker
													label="Asset"
													value={field.value || 0}
													onChange={field.onChange}
												/>
											)}
										/>
									</Grid2>
								) : withAssetFilter === "selector" ? (
									<Grid2 size={{ md: 4, xs: 12 }}>
										<Controller
											name="AssetId"
											control={control}
											render={({ field }) => (
												<AssetsFilter
													value={field.value as number | null}
													onChange={field.onChange}
													filterAreaAssets={filterAssets === "area"}
												/>
											)}
										/>
									</Grid2>
								) : null
							) : null}
						</Grid2>
						<Grid2
							size={{ md: 12 }}
							height={"100%"}
							flexGrow={1}
							overflow="auto"
						>
							<Controller
								name="Content"
								control={control}
								render={({ field: { value, onChange } }) => (
									<ContentInput
										UpdatedBy={updatedBy as string}
										UpdateTimestamp={updateTimestamp as Date}
										onChange={(val) => onChange(val)}
										value={value as string}
										isEditing={show === "edit"}
										isNewEntry={isNewEntry}
										entryId={entryIdValue as number}
										entryData={{
											assetId: assetId as number,
											entryId: entryIdValue as number,
											shiftId: shiftId as number,
											title: title as string,
											content: value as string,
											author: author as string,
											createTimestamp: isNewEntry
												? new Date()
												: (createTimestamp as Date),
										}}
										onEntrySubmitted={handleEntrySubmitted}
										allowAttachments={allowAttachments}
									/>
								)}
							/>
						</Grid2>
						<Grid2 container size={{ xs: 12 }}>
							<Grid2 container size={{ md: 6, xs: 12 }} spacing={1}>
								<Grid2 size={{ md: 6, xs: 12 }}>
									<Button
										fullWidth
										variant="contained"
										color="inherit"
										onClick={() => onHide()}
									>
										Close
									</Button>
								</Grid2>
								<Grid2 size={{ md: 6, xs: 12 }}>
									<Button
										fullWidth
										variant="contained"
										color="primary"
										onClick={() => onChangeShow()}
									>
										{show === "edit" ? "Preview" : "Edit"}
									</Button>
								</Grid2>
							</Grid2>
							<Grid2
								container
								size={{ md: 6, xs: 12 }}
								style={{ display: "flex", justifyContent: "flex-end" }}
							>
								{entryActions && (
									<Grid2 size={{ md: 6, xs: 12 }}>
										{entryActions({
											entry: entry ?? ENTRY_INITIAL_VALUES,
											isNewEntry,
											isDirty: isFormDirty,
											requestSave: () => handleSubmit(onSubmit)(),
										})}
									</Grid2>
								)}
								<Grid2 size={{ md: 6, xs: 12 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading && <CircularProgress size="1rem" />
										}
										disabled={isSubmitLoading || !isFormDirty}
										variant="contained"
										color="primary"
										type="submit"
									>
										{isNewEntry ? "Create" : "Save"}
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</Grid2>
				</Paper>
			) : null}
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
			<ConfirmationDialogRaw
				open={showConfirmationDialog}
				onClose={handleConfirmationClose}
				title="Shift Submission Confirmation"
				message={`You are about to ${
					isNewEntry ? "create" : "save"
				} an entry for ${moment(shiftTimestamp).format(
					"MM/DD/YYYY",
				)} ${shift} - ${crew}. Are you sure you want to proceed?`}
				suffixTitle="Logbook Entry"
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
